
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import React from "react";
import useTranslation from "next-translate/useTranslation";
import Trans from "next-translate/Trans";
import { useTheme } from "@/hooks/useTheme";

import { Converter } from "@/containers/Converter";
import { Head } from "@/components/Head";

import { MagicianSection } from "@/sections/MagicianSection";
import { ShareDesignSection } from "@/sections/ShareDesignSection";
import { ConversionSection } from "@/sections/ConversionSection";
import { TestimonialsSection } from "@/sections/TestimonialsSection";
import { SupportFeaturesSection } from "@/sections/SupportFeaturesSection";
import { HowItWorksStepsSection } from "@/sections/HowItWorksStepsSection";
import { CTASection } from "@/sections/CTASection";
import { TrustedBySection } from "@/sections/TrustedBySection";

const FigmaToWebflow = () => {
  const { t } = useTranslation();
  const { colors } = useTheme();

  return (
    <>
      <Head
        pageNamespace="page/FigmaToWebflow"
        ogImage="/images/figma2webflow-og-image.jpg"
      />

      <MagicianSection
        title={t("page/FigmaToWebflow:title")}
        subtitle={t("page/FigmaToWebflow:subtitle")}
        magicianLogoFile="webflow"
        cta={{
          title: t("section/magicians:seeHowItWorks"),
          scrollToId: "HowItWorksStepsSection",
        }}
      >
        <Converter disabled />
      </MagicianSection>

      <TrustedBySection />

      <ConversionSection
        title={t("page/FigmaToWebflow:section/conversion.title")}
        iconBadges={[
          {
            iconType: "SHEETS",
            text: t("section/conversion:badges.supportsAll"),
          },
          {
            iconType: "SYNC-ICON",
            text: t("section/conversion:badges.convertsComponentsSymbols", {
              format: "XD",
            }),
          },
          {
            iconType: "CHECK-CIRCLED-EMPTY",
            text: t("section/conversion:badges.accuracy"),
          },
        ]}
        elements={{
          from: {
            iconType: "FIGMA",
            image: "/images/figma-webflow-preview.jpg",
            title: "Figma Demo Preview",
          },
          to: {
            iconType: "WEBFLOW",
            image: "/images/webflow-preview.jpg",
            title: "Webflow Demo Preview",
          },
        }}
      />

      <ShareDesignSection
        id="ShareDesignSection"
        title={
          ((
            <Trans
              i18nKey="page/FigmaToWebflow:section/shareDesign.title"
              components={[<span key="0" style={{ color: colors.ORANGE }} />]}
            />
          ) as unknown) as string
        }
        image={{
          src: "/images/figma-to-webflow-converted-file-elements.jpg",
          alt: t("page/FigmaToWebflow:section/shareDesign.imageAlt"),
        }}
        backgroundColor={colors.LIGHT_GRAY1}
        cta={{
          title: t("page/FigmaToWebflow:section/shareDesign.ctaButton"),
        }}
        elements={[
          {
            id: "ELEMENT#1",
            iconType: "SPREAD-ICON",
            title: t(
              "page/FigmaToWebflow:section/shareDesign.elements.pathElementsTitle",
            ),
            text: t(
              "page/FigmaToWebflow:section/shareDesign.elements.pathElementsText",
            ),
          },
          {
            id: "ELEMENT#2",
            iconType: "FOLDER-SIMPLE-ICON",
            title: t(
              "page/FigmaToWebflow:section/shareDesign.elements.folderStructureTitle",
            ),
            text: t(
              "page/FigmaToWebflow:section/shareDesign.elements.folderStructureText",
            ),
          },
          {
            id: "ELEMENT#3",
            iconType: "CHECK-CIRCLED-EMPTY",
            title: t(
              "page/FigmaToWebflow:section/shareDesign.elements.imageSupportTitle",
            ),
            text: t(
              "page/FigmaToWebflow:section/shareDesign.elements.imageSupportText",
            ),
          },
        ]}
      />

      <SupportFeaturesSection
        title={t("page/FigmaToWebflow:section/supportFeatures.title")}
        subtitle={t("page/FigmaToWebflow:section/supportFeatures.subtitle")}
        cta={{
          title: t("common:actions.doNow", { do: t("common:actions.signUp") }),
        }}
        elements={[
          {
            src: "/images/feature-layers.png",
            title: t(
              "page/FigmaToWebflow:section/supportFeatures.features.layers.title",
            ),
            text: t(
              "page/FigmaToWebflow:section/supportFeatures.features.layers.text",
            ),
          },
          {
            src: "/images/feature-pages.png",
            title: t(
              "page/FigmaToWebflow:section/supportFeatures.features.pages.title",
            ),
            text: t(
              "page/FigmaToWebflow:section/supportFeatures.features.pages.text",
            ),
          },
          {
            src: "/images/feature-symbols.png",
            title: t(
              "page/FigmaToWebflow:section/supportFeatures.features.symbols.title",
            ),
            text: t(
              "page/FigmaToWebflow:section/supportFeatures.features.symbols.text",
            ),
          },
        ]}
      />
      <HowItWorksStepsSection
        title={t("page/FigmaToWebflow:section/howItWorksSteps.title")}
        subtitle={t("page/FigmaToWebflow:section/howItWorksSteps.subtitle")}
        sourceSteps={{
          conversionType: "figma",
          icon: "FIGMA",
          steps: [
            {
              id: 0,
              title: t(
                "page/FigmaToWebflow:section/howItWorksSteps.sourceSteps.step1.title",
              ),
              text: t(
                "page/FigmaToWebflow:section/howItWorksSteps.sourceSteps.step1.text",
              ),
            },
            {
              id: 1,
              title: t(
                "page/FigmaToWebflow:section/howItWorksSteps.sourceSteps.step2.title",
              ),
              text: t(
                "page/FigmaToWebflow:section/howItWorksSteps.sourceSteps.step2.text",
              ),
            },
            {
              id: 2,
              title: t(
                "page/FigmaToWebflow:section/howItWorksSteps.sourceSteps.step3.title",
              ),
              text: t(
                "page/FigmaToWebflow:section/howItWorksSteps.sourceSteps.step3.text",
              ),
            },
          ],
        }}
        targetSteps={{
          conversionType: "webflow",
          icon: "WEBFLOW",
          steps: [
            {
              id: 0,
              title: t(
                "page/FigmaToWebflow:section/howItWorksSteps.targetSteps.step1.title",
              ),
              text: t(
                "page/FigmaToWebflow:section/howItWorksSteps.targetSteps.step1.text",
              ),
            },
            {
              id: 1,
              title: t(
                "page/FigmaToWebflow:section/howItWorksSteps.targetSteps.step2.title",
              ),
              text: t(
                "page/FigmaToWebflow:section/howItWorksSteps.targetSteps.step2.text",
              ),
            },
          ],
        }}
      />
      <TestimonialsSection />
      <CTASection
        title={t("section/cta:titles.figmaToWebflowPage")}
        href="#upload"
        isBetaFormat
      />
    </>
  );
};

// Temporal workaround for https://github.com/vercel/next.js/discussions/18396
 async function _getStaticProps() {
  return {
    props: {},
  };
}

export default FigmaToWebflow;


// @ts-ignore
    export async function getStaticProps(ctx) {
// @ts-ignore
        let res = _getStaticProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/converter/figma-to-webflow',
// @ts-ignore
              loaderName: 'getStaticProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  